import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, ExternalLink, Image as ImageIcon, LifeBuoy, Mail, Paperclip, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  api, errorMessage, fmtDateTime, humanize, timeAgo, useTickets, useUsers,
} from './lib';
import {
  Btn, CopyButton, EmptyState, ErrorState, KeyValue, Page, Pagination, Panel, Pill, RefreshButton,
  RowsSkeleton, SearchInput, Segmented, usePagination,
} from './ui';

const STATUS = {
  open: { label: 'Open', tone: 'danger' },
  in_progress: { label: 'In progress', tone: 'warning' },
  closed: { label: 'Closed', tone: 'success' },
};
const ticketId = (t) => t.ticket_id || t.id;

function replyHref(ticket) {
  const subject = `Re: ${ticket.subject || 'Your Better Husband support request'}`;
  const quoted = (ticket.description || '').split('\n').map((l) => `> ${l}`).join('\n');
  const body = `\n\n---\nOn ${fmtDateTime(ticket.created_at)} you wrote:\n${quoted}`;
  return `mailto:${ticket.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function TicketSheet({ ticket, onClose, onStatus, updating, onOpenCustomer, hasCustomer }) {
  const [preview, setPreview] = useState(null);
  const images = Array.isArray(ticket?.images) ? ticket.images.filter(Boolean) : [];
  return (
    <>
      <Sheet open={!!ticket} onOpenChange={(open) => { if (!open) onClose(); }}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
          {ticket && (
            <>
              <div className="border-b border-border px-5 pb-4 pt-5 sm:px-6">
                <div className="pr-8">
                  <SheetTitle className="text-base font-semibold leading-snug">{ticket.subject || 'No subject'}</SheetTitle>
                  <SheetDescription className="mt-1 text-sm">
                    From {ticket.email || 'an anonymous user'} · {timeAgo(ticket.created_at)}
                  </SheetDescription>
                </div>
                <div className="mt-4 space-y-2">
                  <p className="text-xs text-muted-foreground">Status — the customer gets a notification when it changes</p>
                  <Segmented
                    size="md"
                    value={ticket.status}
                    onChange={(s) => s !== ticket.status && onStatus(ticket, s)}
                    options={Object.entries(STATUS).map(([value, s]) => ({ value, label: s.label }))}
                  />
                  {updating && <p className="text-xs text-muted-foreground">Saving…</p>}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {ticket.email && (
                    <Btn size="sm" asChild><a href={replyHref(ticket)}><Mail className="h-3.5 w-3.5" />Reply by email</a></Btn>
                  )}
                  {hasCustomer && (
                    <Btn variant="outline" size="sm" onClick={() => onOpenCustomer(ticket.user_id)}>
                      <User className="h-3.5 w-3.5" />Open customer
                    </Btn>
                  )}
                  <CopyButton text={ticketId(ticket)} label="Copy ID" className="h-8 border border-border px-3" />
                </div>
              </div>
              <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-5 sm:px-6">
                <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
                  {ticket.description || ticket.message || 'No message.'}
                </p>
                {images.length > 0 && (
                  <section>
                    <p className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Paperclip className="h-3.5 w-3.5" />{images.length} attachment{images.length > 1 ? 's' : ''}
                    </p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {images.map((url, i) => (
                        <button key={url} type="button" onClick={() => setPreview(url)} className="group relative overflow-hidden rounded-lg border border-border">
                          <img src={url} alt={`Attachment ${i + 1}`} className="h-28 w-full object-cover transition-opacity group-hover:opacity-80" />
                        </button>
                      ))}
                    </div>
                  </section>
                )}
                <KeyValue items={[
                  { label: 'Type', value: humanize(ticket.type) },
                  { label: 'Received', value: fmtDateTime(ticket.created_at) },
                  { label: 'Last updated', value: fmtDateTime(ticket.updated_at) },
                  { label: 'Ticket ID', value: ticketId(ticket), mono: true },
                ]} />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={!!preview} onOpenChange={(open) => { if (!open) setPreview(null); }}>
        <DialogContent
          className="max-w-4xl border-0 bg-transparent p-0 shadow-none [&>button]:right-2 [&>button]:top-2 [&>button]:rounded-full [&>button]:bg-white [&>button]:p-1.5 [&>button]:opacity-100"
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">Attachment</DialogTitle>
          {preview && (
            <div className="flex flex-col items-center gap-2">
              <img src={preview} alt="Attachment" className="max-h-[80vh] max-w-full rounded-lg object-contain" />
              <a href={preview} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-white underline-offset-2 hover:underline">
                Open original <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function SupportPanel({ selectedId, onSelect, onOpenCustomer }) {
  const { data: tickets, error, loading, refetch, updatedAt, setData } = useTickets();
  const { data: users } = useUsers();
  const [filter, setFilter] = useState('open');
  const [query, setQuery] = useState('');
  const [updating, setUpdating] = useState(null);
  const list = useMemo(() => tickets || [], [tickets]);

  const counts = useMemo(() => ({
    open: list.filter((t) => t.status === 'open').length,
    in_progress: list.filter((t) => t.status === 'in_progress').length,
    closed: list.filter((t) => t.status === 'closed').length,
    all: list.length,
  }), [list]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return list
      .filter((t) => filter === 'all' || t.status === filter)
      .filter((t) => !q
        || (t.subject || '').toLowerCase().includes(q)
        || (t.description || '').toLowerCase().includes(q)
        || (t.email || '').toLowerCase().includes(q));
  }, [list, filter, query]);
  const pager = usePagination(filtered, 30, `${filter}|${query}`);

  const userIds = useMemo(() => new Set((users || []).map((u) => u.user_id)), [users]);
  const selected = list.find((t) => ticketId(t) === selectedId) || null;

  const setStatus = async (ticket, status) => {
    const id = ticketId(ticket);
    const previous = ticket.status;
    setUpdating(id);
    setData((all) => (all || []).map((t) => (ticketId(t) === id ? { ...t, status } : t)));
    try {
      await api.patch(`/support/${id}`, { status });
      toast.success(`Marked as ${STATUS[status].label.toLowerCase()}. The customer was notified.`);
    } catch (err) {
      setData((all) => (all || []).map((t) => (ticketId(t) === id ? { ...t, status: previous } : t)));
      toast.error(errorMessage(err));
    } finally {
      setUpdating(null);
    }
  };

  return (
    <Page
      title="Support"
      description={tickets ? `${counts.open} open, ${counts.in_progress} in progress, ${counts.closed} closed.` : 'Support tickets sent from the app and the website.'}
      actions={<RefreshButton onClick={refetch} loading={loading} updatedAt={updatedAt} />}
    >
      {error && <ErrorState error={error} onRetry={refetch} />}
      <Panel flush>
        <div className="flex flex-col gap-3 border-b border-border p-3 sm:flex-row sm:items-center sm:justify-between">
          <SearchInput value={query} onChange={setQuery} placeholder="Subject, message or email" />
          <Segmented
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'open', label: 'Open', count: counts.open },
              { value: 'in_progress', label: 'In progress', count: counts.in_progress },
              { value: 'closed', label: 'Closed', count: counts.closed },
              { value: 'all', label: 'All', count: counts.all },
            ]}
          />
        </div>
        {!tickets && loading ? (
          <RowsSkeleton rows={6} />
        ) : filtered.length === 0 ? (
          filter === 'open' && !query ? (
            <EmptyState
              icon={CheckCircle2}
              title="No open tickets"
              description="Everything has been answered."
              action={list.length > 0 && <Btn variant="outline" size="sm" onClick={() => setFilter('all')}>Show all tickets</Btn>}
            />
          ) : (
            <EmptyState icon={LifeBuoy} title="No tickets match" description="Try another search or status." />
          )
        ) : (
          <>
            <ul className="divide-y divide-border">
              {pager.slice.map((t) => {
                const s = STATUS[t.status] || STATUS.open;
                const attachments = Array.isArray(t.images) ? t.images.length : 0;
                return (
                  <li key={ticketId(t)}>
                    <button
                      type="button"
                      onClick={() => onSelect(ticketId(t))}
                      className={cn(
                        'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/50',
                        selectedId === ticketId(t) && 'bg-secondary/60',
                      )}
                    >
                      <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', {
                        'bg-red-500': t.status === 'open',
                        'bg-amber-500': t.status === 'in_progress',
                        'bg-emerald-500': t.status === 'closed',
                      })} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate text-sm font-medium text-foreground">{t.subject || 'No subject'}</p>
                          {t.type && <Pill className="hidden sm:inline-flex">{humanize(t.type)}</Pill>}
                        </div>
                        <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{t.description || t.message}</p>
                        <p className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                          <span className="truncate">{t.email || 'Anonymous'}</span>
                          {attachments > 0 && <span className="inline-flex items-center gap-0.5"><ImageIcon className="h-3 w-3" />{attachments}</span>}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <Pill tone={s.tone}>{s.label}</Pill>
                        <span className="text-[11px] text-muted-foreground">{timeAgo(t.created_at)}</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
            <Pagination {...pager} noun="tickets" />
          </>
        )}
      </Panel>

      <TicketSheet
        ticket={selected}
        onClose={() => onSelect(null)}
        onStatus={setStatus}
        updating={!!selected && updating === ticketId(selected)}
        onOpenCustomer={onOpenCustomer}
        hasCustomer={!!selected?.user_id && userIds.has(selected.user_id)}
      />
    </Page>
  );
}
